// S-71 (本体第17条): 契約終了時データライフサイクルの純関数。
//
// 「契約終了 → 救済期間30日（エクスポート可能を維持）→ 経過後に利用者データ削除（purge）」
// という契約義務を制御するための判定ロジックを 1 箇所に集約する。Firebase / Web SDK 非依存
// （Iso8601 文字列のみを扱う pure 関数）なので functions / apps の双方から import できる。
import { TENANT_DATA_GRACE_PERIOD_DAYS } from './types.js';
/** 救済期間（日数）のミリ秒換算。`graceUntil` 不在時に `contractEndedAt` から導出するのに使う。 */
const GRACE_PERIOD_MS = TENANT_DATA_GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000;
/**
 * テナント status の許可された遷移マトリクス。
 * - active    → suspended（一時停止） / archived（契約終了）
 * - suspended → active（再開）        / archived（契約終了）
 * - archived  → active（契約復活・誤操作復旧）
 *
 * 同一 status への遷移（no-op）は許可しない。archived → suspended は意味を持たないため不許可。
 */
const ALLOWED_TENANT_STATUS_TRANSITIONS = {
    active: ['suspended', 'archived'],
    suspended: ['active', 'archived'],
    archived: ['active'],
};
/** `from` から `to` への status 遷移が許可されているか。 */
export function isValidTenantStatusTransition(from, to) {
    if (from === to)
        return false;
    return ALLOWED_TENANT_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}
/**
 * `archived`（契約終了）への遷移かどうか。
 * このとき呼び出し側で `contractEndedAt` / `graceUntil` を設定する。
 */
export function isContractTerminationTransition(to) {
    return to === 'archived';
}
/**
 * 救済期限（ms）を解決する。`graceUntil` を優先し、不在/不正な場合は `contractEndedAt + 30日`
 * から導出する。両方とも無い/不正なら null（＝期限を確定できない）。
 *
 * これにより、`tenantSetStatus` 以外の経路（手動コンソール編集 / 旧 archived テナント /
 * 将来の migration）で `graceUntil` が欠落した archived テナントでも、`contractEndedAt` さえ
 * あれば救済期限を判定でき、「恒久的に purge 不可」状態を避けられる（review S-71 #1）。
 */
function resolveGraceDeadlineMs(tenant) {
    const fromGrace = safeParseMs(tenant.graceUntil);
    if (fromGrace !== null)
        return fromGrace;
    const fromEnded = safeParseMs(tenant.contractEndedAt);
    if (fromEnded !== null)
        return fromEnded + GRACE_PERIOD_MS;
    return null;
}
/**
 * 救済期間が経過しているか（archived かつ救済期限を過ぎている）。
 *
 * fail-closed: archived でない / 救済期限を確定できない / now が不正な場合は「未経過」を返す
 * （誤って purge 可能と判定しないため）。`graceUntil`/`contractEndedAt`/`nowIso` が不正形式でも
 * throw せず安全側に倒す（render 中の呼び出しでクラッシュさせない / review S-71 #4）。
 */
export function isTenantGraceExpired(tenant, nowIso) {
    if (tenant.status !== 'archived')
        return false;
    const deadline = resolveGraceDeadlineMs(tenant);
    if (deadline === null)
        return false;
    const nowMs = safeParseMs(nowIso);
    if (nowMs === null)
        return false;
    return nowMs >= deadline;
}
/**
 * 利用者データのエクスポートが可能か（本体第17条2項のデータ救済）。
 *
 * - active / suspended: 常に可
 * - archived: 救済期間中（救済期限まで）は可、経過後は不可
 * - archived だが救済期限を確定できない / now が不正: データ救済を優先して fail-open で可とする
 */
export function canExportTenantData(tenant, nowIso) {
    if (tenant.status !== 'archived')
        return true;
    const deadline = resolveGraceDeadlineMs(tenant);
    if (deadline === null)
        return true;
    const nowMs = safeParseMs(nowIso);
    if (nowMs === null)
        return true;
    return nowMs <= deadline;
}
/**
 * テナント単位のデータ削除（purge）が可能か（本体第17条3項）。
 *
 * fail-closed: archived かつ救済期限が確定済みかつ救済期間を経過した場合のみ true。
 * active / suspended / 救済期間中 / 期限不確定は不可。
 */
export function canPurgeTenant(tenant, nowIso) {
    return isTenantGraceExpired(tenant, nowIso);
}
/** Iso8601 文字列をミリ秒に変換。パースできなければ null を返す（throw しない＝判定で安全側に倒す）。 */
function safeParseMs(iso) {
    if (!iso)
        return null;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? null : ms;
}
//# sourceMappingURL=tenantLifecycle.js.map